import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {Plugin, PluginKey, TextSelection} from 'prosemirror-state';
import {Paper, ThemeProvider, useTheme} from '@mui/material';
import theme from '../../../theme';
import UserMentionInfoCard from '../components/UserMentionInfoCard';
import HashtagInfoCard from '../components/HashtagInfoCard';
import {WikidataEntityCard, useHydratedEntityDetails} from '../components/WikidataEntityDialog';
import {MentionHoverContent} from '../../entityMentions/MentionHoverCard';
import {
    normalizeWikidataEntityDomEl,
    shouldSuppressEntityHover,
} from '../../entityMentions/entityHoverSuppression';
import {resolveFloatingPanelLayout} from '../components/floatingPanelPosition';
import {
    buildInteractionPayload,
    findWikidataMarkRange,
    getPayloadSignature,
    resolveTargetFromSelection,
} from './inlineInteractionPlugin';
import {isLinkedEntityRef} from '../../../utils/entityLinks';
import {readUnlinkedMentionsDisplayedSync} from '../../../hooks/entityDisplayConfig';
import {isPlaceholderEntityType} from '../../../utils/entityTypeMeta';

export const inlineInteractionPopoverPluginKey = new PluginKey('inlineInteractionPopover');

/**
 * A wikidata payload is "linked" when its id is a Wikidata QID or custom D ref.
 * Recognised-but-unlinked mentions (yellow) have a null / non-linked id and should
 * open the entity search directly instead of the info popover.
 */
function isLinkedWikidataPayload(payload) {
    return isLinkedEntityRef(payload?.id);
}

/**
 * Decide whether the caret-driven render should open the Wikidata search for an unlinked
 * (yellow) mention. Search must open ONLY on an explicit mouse click on the mention, never from
 * caret/selection changes (arrow keys, focus, programmatic selection) or hover. The click intent
 * is armed on mousedown over an unlinked mention and consumed once here.
 *
 * @param {boolean} isUnlinkedMention - current caret target is an unlinked wikidata mention
 * @param {boolean} clickIntent - the caret landing was caused by an explicit click on the mention
 * @returns {boolean}
 */
export function shouldOpenUnlinkedSearch(isUnlinkedMention, clickIntent) {
    if (!readUnlinkedMentionsDisplayedSync()) return false;
    return Boolean(isUnlinkedMention && clickIntent);
}

/**
 * Caret-driven inline popovers (entity info card, mention/hashtag cards) should only render
 * while the editor has DOM focus. On initial page load the view starts blurred so nothing
 * appears selected until the user clicks or tabs into the editor.
 *
 * @param {boolean} editorHasFocus
 * @returns {boolean}
 */
export function shouldShowCaretDrivenPopover(editorHasFocus) {
    return Boolean(editorHasFocus);
}

export {shouldSuppressEntityHover} from '../../entityMentions/entityHoverSuppression';

function getWikidataHoverSuppressionOptions(view, payload) {
    if (!view || !payload || payload.type !== 'wikidata') return {};
    const markRange = findWikidataMarkRange(view.state, payload.position, payload.uid);
    return markRange ? {markRange} : {};
}

function shouldSuppressEditorEntityHover(view, ...payloads) {
    const hoverTarget = payloads[payloads.length - 1];
    const activePayloads = payloads.slice(0, -1).filter(Boolean);
    return activePayloads.some((payload) => (
        shouldSuppressEntityHover(payload, hoverTarget, getWikidataHoverSuppressionOptions(view, payload))
    ));
}

function getPanelConfig(payload) {
    if (!payload) return {width: 320, sx: {}};

    if (payload.type === 'mention') {
        return {
            width: 320,
            sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 3,
                p: 0,
                minWidth: 280,
                maxWidth: 360,
            },
        };
    }

    if (payload.type === 'hashtag') {
        return {
            width: 300,
            sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 3,
            },
        };
    }

    return {
        width: 360,
        sx: {
            p: 1.5,
        },
    };
}

function sameAnchorPosition(left, right) {
    if (left === right) return true;
    if (!left || !right) return false;
    return left.left === right.left && left.top === right.top;
}

function samePayloadContent(left, right) {
    if (left === right) return true;
    if (!left || !right) return false;
    if (left.type !== right.type) return false;

    return left.trigger === right.trigger
        && left.id === right.id
        && left.uid === right.uid
        && left.label === right.label
        && left.name === right.name
        && left.email === right.email
        && left.position === right.position
        && left.nodePos === right.nodePos
        && left.docId === right.docId
        && left.url === right.url
        && left.description === right.description
        && left.imageUrl === right.imageUrl
        && left.anchorEl === right.anchorEl
        && sameAnchorPosition(left.anchorPosition, right.anchorPosition)
        && left.view === right.view;
}

export function reuseInlinePopoverPayload(previousPayload, nextPayload) {
    if (!previousPayload || !nextPayload) {
        return nextPayload;
    }

    return samePayloadContent(previousPayload, nextPayload) ? previousPayload : nextPayload;
}

function InlinePluginFloatingPanel({payload, onClose, children}) {
    const panelRef = useRef(null);
    const [layout, setLayout] = useState({visible: false, left: 0, top: 0});
    const theme = useTheme();
    const {width, sx} = getPanelConfig(payload);
    const anchorEl = payload?.anchorEl || null;
    const anchorPosition = payload?.anchorPosition || null;

    const updatePosition = useCallback(() => {
        if (!payload) return;
        const panelWidth = panelRef.current?.offsetWidth || width;
        const panelHeight = panelRef.current?.offsetHeight || 0;
        setLayout(resolveFloatingPanelLayout({
            anchorEl,
            anchorPosition,
            view: payload.view || null,
            position: payload.position,
            panelWidth,
            panelHeight,
            viewDom: payload.view?.dom || null,
        }));
    }, [anchorEl, anchorPosition, payload, width]);

    useEffect(() => {
        if (!payload) return undefined;

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [payload, updatePosition]);

    useEffect(() => {
        if (!payload || layout.visible) return undefined;
        const raf = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(raf);
    }, [payload, layout.visible, updatePosition]);

    useEffect(() => {
        if (!payload) return undefined;

        const onPointerDown = (event) => {
            const target = event.target;
            if (panelRef.current?.contains(target)) return;
            if (anchorEl?.contains?.(target)) return;
            onClose?.(event, 'backdropClick');
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [anchorEl, onClose, payload]);

    useEffect(() => {
        if (!payload) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.(event, 'escapeKeyDown');
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, payload]);

    if (!payload || (!anchorEl && !anchorPosition)) {
        return null;
    }

    return (
        <Paper
            ref={panelRef}
            elevation={6}
            onMouseDown={(event) => {
                event.preventDefault();
            }}
            sx={{
                position: 'fixed',
                left: layout.left,
                top: layout.top,
                visibility: layout.visible ? 'visible' : 'hidden',
                zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
                pointerEvents: layout.visible ? 'auto' : 'none',
                width,
                maxWidth: '90vw',
                border: '1px solid',
                borderColor: theme.palette.divider,
                ...sx,
            }}
        >
            {children}
        </Paper>
    );
}

function WikidataNativeTooltipPanel({payload, onClose, children}) {
    const panelRef = useRef(null);
    const [layout, setLayout] = useState({visible: false, left: 0, top: 0});
    const theme = useTheme();
    const {width, sx} = getPanelConfig(payload);
    const anchorEl = payload?.anchorEl || null;

    const updatePosition = useCallback(() => {
        if (!payload) return;
        const panelWidth = panelRef.current?.offsetWidth || width;
        const panelHeight = panelRef.current?.offsetHeight || 0;
        setLayout(resolveFloatingPanelLayout({
            anchorEl,
            anchorPosition: payload.anchorPosition || null,
            view: payload.view || null,
            position: payload.position,
            panelWidth,
            panelHeight,
            viewDom: payload.view?.dom || null,
        }));
    }, [anchorEl, payload, width]);

    useEffect(() => {
        if (!payload) return undefined;

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [payload, updatePosition]);

    useEffect(() => {
        if (!payload || layout.visible) return undefined;
        const raf = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(raf);
    }, [payload, layout.visible, updatePosition]);

    useEffect(() => {
        if (!payload) return undefined;

        const onPointerDown = (event) => {
            const target = event.target;
            if (panelRef.current?.contains(target)) return;
            if (anchorEl?.contains?.(target)) return;
            onClose?.(event, 'backdropClick');
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [anchorEl, onClose, payload]);

    useEffect(() => {
        if (!payload) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.(event, 'escapeKeyDown');
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, payload]);

    if (!payload) {
        return null;
    }

    return (
        <Paper
            ref={panelRef}
            elevation={6}
            onMouseDown={(event) => {
                event.preventDefault();
            }}
            sx={{
                position: 'fixed',
                left: layout.left,
                top: layout.top,
                visibility: layout.visible ? 'visible' : 'hidden',
                zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
                pointerEvents: layout.visible ? 'auto' : 'none',
                width,
                maxWidth: '90vw',
                border: '1px solid',
                borderColor: theme.palette.divider,
                ...sx,
            }}
        >
            {children}
        </Paper>
    );
}

export function MentionHoverPanel({hover, viewDom = null}) {
    const panelRef = useRef(null);
    const [layout, setLayout] = useState({visible: false, left: 0, top: 0});
    const {id = null, entityType: markEntityType = null} = hover || {};
    const linkedId = id && isLinkedEntityRef(id) ? id : null;
    const {kbEntityType, nerEntityType, canonicalName: kbCanonicalName} = useHydratedEntityDetails(
        linkedId,
        markEntityType,
        null,
    );

    const updatePosition = useCallback(() => {
        if (!hover?.anchorEl) return;
        const panelWidth = panelRef.current?.offsetWidth || 280;
        const panelHeight = panelRef.current?.offsetHeight || 0;
        setLayout(resolveFloatingPanelLayout({
            anchorEl: hover.anchorEl,
            panelWidth,
            panelHeight,
            viewDom,
        }));
    }, [hover, viewDom]);

    useEffect(() => {
        if (!hover) return undefined;
        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [hover, updatePosition]);

    if (!hover || !layout.visible) return null;
    const {name, confidence, nerScore, linkingScore} = hover;
    const displayName = kbCanonicalName || name;
    const entityType = nerEntityType || (isPlaceholderEntityType(markEntityType) ? null : markEntityType);
    return (
        <Paper
            ref={panelRef}
            elevation={4}
            sx={{
                position: 'fixed',
                left: layout.left,
                top: layout.top,
                zIndex: (muiTheme) => muiTheme.zIndex.tooltip + 1,
                pointerEvents: 'none',
                p: 1,
                maxWidth: 280,
            }}
        >
            <MentionHoverContent name={displayName} entityType={entityType} kbEntityType={kbEntityType} confidence={confidence} nerScore={nerScore} linkingScore={linkingScore}/>
        </Paper>
    );
}

function InlineInteractionPopoverRenderer({payload, onClose}) {
    if (!payload) return null;

    if (payload.type === 'mention') {
        return (
            <InlinePluginFloatingPanel payload={payload} onClose={onClose}>
                <UserMentionInfoCard
                    userData={{
                        id: payload.id,
                        uid: payload.uid,
                        name: payload.name,
                        label: payload.label,
                        email: payload.email,
                        position: payload.position,
                        headshot: payload.headshot,
                    }}
                    onMessageOpened={onClose}
                />
            </InlinePluginFloatingPanel>
        );
    }

    if (payload.type === 'hashtag') {
        return (
            <InlinePluginFloatingPanel payload={payload} onClose={onClose}>
                <HashtagInfoCard tagData={{id: payload.id, label: payload.label, docId: payload.docId}} />
            </InlinePluginFloatingPanel>
        );
    }

    if (payload.type === 'wikidata') {
        // Unlinked (yellow) mentions never render the info card — the plugin view opens
        // the Wikidata search directly. Only linked entities show the info popover.
        if (!isLinkedWikidataPayload(payload)) {
            return null;
        }
        return (
            <WikidataNativeTooltipPanel payload={payload} onClose={onClose}>
                <WikidataEntityCard
                    open
                    anchorEl={payload.anchorEl}
                    anchorPosition={payload.anchorPosition}
                    id={payload.id}
                    uid={payload.uid}
                    label={payload.label}
                    url={payload.url}
                    description={payload.description}
                    entityType={payload.entityType}
                    confidence={payload.confidence}
                    nerScore={payload.nerScore}
                    linkingScore={payload.linkingScore}
                    imageUrl={payload.imageUrl}
                    onClose={onClose}
                    view={payload.view}
                    position={payload.position}
                />
            </WikidataNativeTooltipPanel>
        );
    }

    return null;
}

class InlineInteractionPopoverView {
    constructor(view) {
        this.view = view;
        this.dismissedSignature = null;
        this.currentSignature = 'none';
        this.searchTriggeredSignature = null;
        // Wikidata search for an unlinked (yellow) mention must open ONLY on an explicit mouse
        // click on the mention — never from caret/selection changes (arrow nav, focus,
        // programmatic selection) or hover. We arm this flag on mousedown over an unlinked
        // mention and consume it on the next render so a single click opens the search exactly once.
        this.pendingClickSearch = false;
        /** @type {object|null} Mousedown target for linked mentions — bridges click→render gap. */
        this.suppressHoverForMention = null;
        this.payload = null;
        this.visiblePayload = null;
        this.host = document.createElement('div');
        this.host.className = 'pm-inline-interaction-popover-host';
        this.host.style.position = 'fixed';
        this.host.style.inset = '0';
        this.host.style.pointerEvents = 'none';
        this.host.style.overflow = 'visible';
        this.host.style.zIndex = '0';
        this.mountTarget = document.body;
        this.mountTarget.appendChild(this.host);
        this.root = createRoot(this.host);
        this.handleClose = this.handleClose.bind(this);

        // Separate root + host for the hover tooltip so it can coexist with the
        // caret-driven info popover without re-rendering it.
        this.hover = null;
        this.hoverHost = document.createElement('div');
        this.hoverHost.className = 'pm-mention-hover-host';
        this.hoverHost.style.position = 'fixed';
        this.hoverHost.style.inset = '0';
        this.hoverHost.style.pointerEvents = 'none';
        this.hoverHost.style.zIndex = '0';
        this.mountTarget.appendChild(this.hoverHost);
        this.hoverRoot = createRoot(this.hoverHost);

        this.handleMouseOver = this.handleMouseOver.bind(this);
        this.handleMouseOut = this.handleMouseOut.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleFocusChange = this.handleFocusChange.bind(this);
        if (view?.dom) {
            view.dom.addEventListener('mouseover', this.handleMouseOver);
            view.dom.addEventListener('mouseout', this.handleMouseOut);
            view.dom.addEventListener('mousedown', this.handleMouseDown);
            view.dom.addEventListener('focus', this.handleFocusChange);
            view.dom.addEventListener('blur', this.handleFocusChange);
        }

        this.render(true);
        this.renderHover();
    }

    handleMouseOver(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const el = target.closest('.wikidata-entity');
        if (!el || !this.view?.dom?.contains(el)) return;

        const hoverEl = normalizeWikidataEntityDomEl(el);
        if (!hoverEl) return;

        const name = hoverEl.getAttribute('data-wikidata-label') || hoverEl.textContent || '';
        const entityType = hoverEl.getAttribute('data-entity-type') || null;
        const rawId = hoverEl.getAttribute('data-wikidata-id');
        const id = typeof rawId === 'string' && isLinkedEntityRef(rawId) ? rawId.trim() : null;
        if (!id && !readUnlinkedMentionsDisplayedSync()) {
            return;
        }
        const uid = hoverEl.getAttribute('data-wikidata-uid') || null;
        const parseScore = (raw) => (raw == null || raw === '' ? null : Number(raw));
        const nerScore = parseScore(hoverEl.getAttribute('data-ner-score')) ?? parseScore(hoverEl.getAttribute('data-confidence'));
        const linkingScore = parseScore(hoverEl.getAttribute('data-linking-score'));
        const confidence = nerScore;

        if (!name && !entityType && nerScore == null && linkingScore == null) {
            return;
        }

        let position = null;
        try {
            position = this.view.posAtDOM(hoverEl, 0);
        } catch (_error) {
            position = null;
        }

        const nextHover = {
            rect: hoverEl.getBoundingClientRect(),
            anchorEl: hoverEl,
            id,
            uid,
            position,
            name,
            entityType,
            confidence,
            nerScore,
            linkingScore,
        };

        if (shouldSuppressEditorEntityHover(
            this.view,
            this.visiblePayload,
            this.suppressHoverForMention,
            nextHover,
        )) {
            if (this.hover) {
                this.hover = null;
                this.renderHover();
            }
            return;
        }

        this.hover = nextHover;
        this.renderHover();
    }

    handleMouseOut(event) {
        const related = event.relatedTarget;
        if (related instanceof Element && related.closest('.wikidata-entity')) {
            return;
        }
        if (!this.hover) return;
        this.hover = null;
        this.renderHover();
    }

    /**
     * Arm the click-to-search intent only when the user explicitly presses on an UNLINKED
     * (yellow) wikidata mention. Linked (QID) mentions open the info card, so they never arm
     * the search. The flag is consumed on the next render() so search opens exactly once per
     * deliberate click — caret moves, arrow navigation, focus, and hover never set it.
     */
    handleMouseDown(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const el = target.closest('.wikidata-entity');
        if (!el || !this.view?.dom?.contains(el)) {
            this.pendingClickSearch = false;
            return;
        }
        // Dismiss the hover preview immediately on press so it never stacks with the
        // click popover — same behaviour as MentionHoverCard in corpus / My Data / Products.
        if (this.hover) {
            this.hover = null;
            this.renderHover();
        }
        const rawId = el.getAttribute('data-wikidata-id');
        const isLinked = isLinkedEntityRef(rawId);
        this.pendingClickSearch = !isLinked && readUnlinkedMentionsDisplayedSync();
        if (isLinked) {
            const clickEl = normalizeWikidataEntityDomEl(el);
            let position = null;
            try {
                position = clickEl ? this.view.posAtDOM(clickEl, 0) : null;
            } catch (_error) {
                position = null;
            }
            this.suppressHoverForMention = {
                type: 'wikidata',
                anchorEl: clickEl,
                uid: clickEl?.getAttribute('data-wikidata-uid') || null,
                position,
            };
        } else {
            this.suppressHoverForMention = null;
        }
    }

    handleFocusChange() {
        this.render(true);
    }

    renderHover() {
        if (this._destroyed) return;
        this.hoverRoot.render(
            <ThemeProvider theme={theme}>
                <MentionHoverPanel hover={this.hover} viewDom={this.view?.dom || null}/>
            </ThemeProvider>
        );
    }

    computePayload() {
        const target = resolveTargetFromSelection(this.view.state);
        return buildInteractionPayload(target, 'caret', this.view);
    }

    handleClose() {
        this.dismissedSignature = this.currentSignature;
        this.suppressHoverForMention = null;
        this.render(true);
    }

    /**
     * For an unlinked (yellow) wikidata mention, open the Wikidata search directly
     * (unified with the data-items / corpus viewers) instead of showing the info card.
     * We expand the selection to the full mention range so the report's existing
     * link pipeline (useWikidataEntity.handleEntitySelect) applies the mark over the
     * whole mention, then dispatch the same `openEntitySearch` event the floating menu
     * uses. A per-signature guard prevents re-firing while the caret stays in the mention.
     */
    maybeOpenSearchForUnlinked(payload, signature) {
        if (!payload || payload.type !== 'wikidata' || isLinkedWikidataPayload(payload)) {
            return false;
        }
        if (this.searchTriggeredSignature === signature) {
            return true; // already opened for this mention; don't reopen
        }

        const view = this.view;
        const range = findWikidataMarkRange(view.state, payload.position, payload.uid);
        if (!range) return false;

        this.searchTriggeredSignature = signature;

        // Defer the selection change + event dispatch out of the current ProseMirror
        // update cycle to avoid dispatching a transaction while handling an update.
        const {from, to} = range;
        queueMicrotask(() => {
            if (this._destroyed) return;
            const v = this.view;
            if (!v || !v.state) return;
            const docSize = v.state.doc.content.size;
            const safeFrom = Math.max(0, Math.min(from, docSize));
            const safeTo = Math.max(0, Math.min(to, docSize));
            if (safeFrom === safeTo) return;

            const tr = v.state.tr.setSelection(TextSelection.create(v.state.doc, safeFrom, safeTo));
            v.dispatch(tr);

            const query = v.state.doc.textBetween(safeFrom, safeTo, ' ').trim()
                || payload.label
                || '';

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('openEntitySearch', {
                    detail: {view: v, query},
                }));
            }
        });
        return true;
    }

    render(force = false) {
        if (this._destroyed) return;
        const payload = reuseInlinePopoverPayload(this.payload, this.computePayload());
        const signature = getPayloadSignature(payload);

        const isUnlinkedMention = payload?.type === 'wikidata' && !isLinkedWikidataPayload(payload);

        // Reset the search guard whenever the caret moves to a different target than
        // the one we last opened search for, so returning to a mention can re-open it.
        if (this.searchTriggeredSignature !== null && signature !== this.searchTriggeredSignature) {
            this.searchTriggeredSignature = null;
        }

        // Unlinked (yellow) wikidata mention → open search ONLY when the caret landed here via an
        // explicit mouse click on the mention. Caret/selection changes (arrow keys, focus,
        // programmatic selection) and hover must never auto-open the Wikidata search.
        const clickIntent = this.pendingClickSearch;
        this.pendingClickSearch = false;
        const openedSearch = shouldOpenUnlinkedSearch(isUnlinkedMention, clickIntent)
            ? this.maybeOpenSearchForUnlinked(payload, signature)
            : false;

        const editorHasFocus = shouldShowCaretDrivenPopover(this.view?.hasFocus?.());
        let visiblePayload = (openedSearch || signature === this.dismissedSignature) ? null : payload;
        if (!editorHasFocus) {
            visiblePayload = null;
        }

        if (!force && signature === this.currentSignature && visiblePayload === this.visiblePayload) {
            return;
        }

        if (signature !== this.currentSignature) {
            this.dismissedSignature = null;
        }

        this.currentSignature = signature;
        this.payload = payload;
        this.visiblePayload = visiblePayload;

        if (this.hover && shouldSuppressEditorEntityHover(
            this.view,
            visiblePayload,
            this.suppressHoverForMention,
            this.hover,
        )) {
            this.hover = null;
            this.renderHover();
        }

        if (!visiblePayload || visiblePayload.type !== 'wikidata') {
            this.suppressHoverForMention = null;
        } else if (this.suppressHoverForMention) {
            const stillSameMention = shouldSuppressEntityHover(
                visiblePayload,
                {
                    anchorEl: this.suppressHoverForMention.anchorEl,
                    uid: this.suppressHoverForMention.uid,
                    position: this.suppressHoverForMention.position,
                },
                getWikidataHoverSuppressionOptions(this.view, visiblePayload),
            );
            if (!stillSameMention) {
                this.suppressHoverForMention = null;
            }
        }

        this.root.render(
            <ThemeProvider theme={theme}>
                <InlineInteractionPopoverRenderer
                    payload={this.visiblePayload}
                    onClose={this.handleClose}
                />
            </ThemeProvider>
        );
    }

    update(view, prevState) {
        this.view = view;
        const selectionChanged = !prevState.selection.eq(view.state.selection);
        const docChanged = prevState.doc !== view.state.doc;
        if (!selectionChanged && !docChanged) return;
        this.render();
    }

    destroy() {
        // React 18 concurrent mode forbids synchronous unmount while a render is in
        // progress (e.g. when ProseMirror tears down the view inside a React effect
        // cleanup). Defer unmount to the next microtask so it always fires after the
        // current render cycle completes.
        this._destroyed = true;
        if (this.view?.dom) {
            this.view.dom.removeEventListener('mouseover', this.handleMouseOver);
            this.view.dom.removeEventListener('mouseout', this.handleMouseOut);
            this.view.dom.removeEventListener('mousedown', this.handleMouseDown);
            this.view.dom.removeEventListener('focus', this.handleFocusChange);
            this.view.dom.removeEventListener('blur', this.handleFocusChange);
        }
        const root = this.root;
        const host = this.host;
        const hoverRoot = this.hoverRoot;
        const hoverHost = this.hoverHost;
        queueMicrotask(() => {
            root.unmount();
            host.remove();
            hoverRoot.unmount();
            hoverHost.remove();
        });
    }
}

export function createInlineInteractionPopoverPlugin() {
    return new Plugin({
        key: inlineInteractionPopoverPluginKey,
        view(view) {
            return new InlineInteractionPopoverView(view);
        },
    });
}

